import { yupResolver } from '@hookform/resolvers/yup';
import { typographyClasses } from '@mui/material';
import Box from '@mui/material/Box';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';

import AutocompleteMaterialTypes from '~components/FormFields/CommonAutocompletes/AutocompleteMaterialTypes';
import AutocompleteScopedCompaniesTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteScopedCompaniesTypeahead';
import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import TextInput from '~components/FormFields/v2/TextInput';
import { Material } from '~hooks/useMaterials/models';
import {
  MaterialDto,
  MaterialDtoAsyncError,
  materialSchema,
  setMaterialSchemaDefaultValues,
} from '~pages/Settings/Administration/MaterialsManagement/materialSchema';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface MaterialFormProps {
  asyncErrors?: MaterialDtoAsyncError[];
  material?: Material;
}

export type MaterialFormHandler = {
  onSubmit: () => Promise<MaterialDto | undefined>;
};

const MaterialForm = forwardRef<MaterialFormHandler, MaterialFormProps>(
  function MaterialForm({ asyncErrors, material }, ref) {
    const { userStore } = useStores();

    const isEditing = Boolean(material);

    const { control, handleSubmit, watch, setError, clearErrors } = useForm<MaterialDto>({
      resolver: yupResolver(materialSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: setMaterialSchemaDefaultValues(userStore, material),
    });

    const companyId = watch('company.value');

    useImperativeHandle(
      ref,
      () => ({
        onSubmit() {
          return new Promise((resolve) => {
            handleSubmit(
              (data) => {
                resolve(data);
              },
              () => {
                resolve(undefined);
              },
            )();
          });
        },
      }),
      [handleSubmit],
    );

    useEffect(() => {
      if (asyncErrors) {
        for (const error of asyncErrors) {
          setError(error.field as keyof MaterialDto, { message: error.message });
        }
      }

      return () => {
        clearErrors();
      };
    }, [asyncErrors]);

    return (
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridAutoColumns: '1fr',
          width: '100%',
        }}
      >
        <Box sx={{ gridColumn: 'span 2' }}>
          <AutocompleteScopedCompaniesTypeahead
            companyId={getEffectiveUserCompanyId(userStore)}
            control={control}
            disableClearable
            isDisabled={isEditing}
            isRequired
            label={t('form_fields.company')}
            name="company"
          />
        </Box>

        <Box>
          <TextInput
            control={control}
            isRequired
            label={t('form_fields.name')}
            name="name"
          />
        </Box>

        <Box>
          <AutocompleteMaterialTypes
            companyId={companyId}
            control={control}
            disableClearable
            label={t('form_fields.material_type')}
            name="type"
          />
        </Box>

        <Box>
          <TextInput
            control={control}
            label={t('form_fields.material_id')}
            name="externalId"
          />
        </Box>

        <Box>
          <CheckboxInput
            control={control}
            label={t('common.inactive')}
            name="isNotActive"
            sx={{
              mt: 2.25,
              [`& .${typographyClasses.root}`]: { fontSize: '13px', fontWeight: 400 },
            }}
          />
        </Box>
      </Box>
    );
  },
);

export default MaterialForm;
